import React, { useRef } from 'react';
import { mergeClasses } from '@fluentui/react-components';
import './GuideComponents.css';

export interface GuideSegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Nombre accesible si `label` no es texto. */
  ariaLabel?: string;
}

export interface GuideSegmentedProps<T extends string> {
  options: GuideSegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Nombre accesible del grupo. */
  ariaLabel: string;
  /** Rótulo visible a la izquierda (13 / secundario), como "Moneda" en la guía. */
  label?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * Control segmentado de la guía ("Lista | Agrupada"): opciones unidas con borde y radio 4; la activa
 * con fondo primario tenue y texto primario bold (alto contraste: relleno amarillo, texto negro).
 * Es un `radiogroup`: una sola parada de Tab y flechas para moverse.
 */
export const GuideSegmented = <T extends string>({ options, value, onChange, ariaLabel, label, className, 'data-testid': testId }: GuideSegmentedProps<T>) => {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = Math.max(0, options.findIndex((option) => option.value === value));

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (index + step + options.length) % options.length;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  const group = (
    <div className="ichtys-segmented" role="radiogroup" aria-label={ariaLabel}>
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={option.ariaLabel}
            tabIndex={index === activeIndex ? 0 : -1}
            className="ichtys-segmented__option"
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={mergeClasses('ichtys-segmented-field', className)} data-testid={testId}>
      {label && (
        <span className="ichtys-segmented-field__label" aria-hidden="true">
          {label}
        </span>
      )}
      {group}
    </div>
  );
};

export default GuideSegmented;
